import { Link, useLoaderData } from 'react-router';
import {
  Button,
  Card,
  CardContent,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo-manager/ui';

import { getActiveRepoId } from '../lib/repo-context';
import { useRepos } from '../lib/repo-context';
import { getSupabase } from '../lib/supabase';

interface SprintRow {
  id: string;
  title: string;
  goal: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
}

interface LoaderData {
  sprints: SprintRow[];
  repoId: string | null;
}

export async function sprintsLoader(): Promise<LoaderData> {
  const supabase = getSupabase();
  const repoId = await getActiveRepoId(supabase);
  if (!repoId) return { sprints: [], repoId: null };
  const { data } = await supabase
    .from('sprints')
    .select('id, title, goal, status, start_date, end_date')
    .eq('repo_id', repoId)
    .order('start_date', { ascending: false, nullsFirst: false })
    .limit(50);
  return { sprints: data ?? [], repoId };
}

export function SprintsPage() {
  const { sprints, repoId } = useLoaderData() as LoaderData;
  const { activeRepo } = useRepos();
  // The loader's repoId is the authoritative scope for this page; the
  // provider's `activeRepo` may briefly disagree on first paint, so we
  // only use it for the display label.
  const scope = activeRepo && activeRepo.id === repoId ? activeRepo : null;
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sprints</h1>
          {scope ? (
            <p className="text-sm text-muted-foreground">
              scoped to <code className="font-mono">{scope.owner}/{scope.name}</code>
            </p>
          ) : !repoId ? (
            <p className="text-sm text-muted-foreground">
              No repo selected — pick one from the header dropdown.
            </p>
          ) : null}
        </div>
        <Button asChild>
          <Link to="/sprints/new">+ Plan sprint</Link>
        </Button>
      </div>
      {sprints.length === 0 ? (
        <EmptyState
          title="No sprints yet"
          description="Plan the first sprint to bring stories into a delivery window."
          action={
            <Button asChild>
              <Link to="/sprints/new">+ Plan sprint</Link>
            </Button>
          }
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Goal</TableHead>
                  <TableHead>Window</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sprints.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>
                      <Link to={`/sprints/${s.id}`} className="font-medium hover:underline">
                        {s.title}
                      </Link>
                    </TableCell>
                    <TableCell className="max-w-md truncate text-sm text-muted-foreground">
                      {s.goal}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {s.start_date ?? '—'} → {s.end_date ?? '—'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={s.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
